import { List } from "immutable";
import { createSelector } from "reselect";

import { getDefaultIconForSyncType } from "../../constants/syncDocumentTypes";
import { syncLocalFieldNames } from "../../constants/syncLocalFields";
import { SYNC_NARRATIVE_FOLDER_IDENTIFIER } from "../../constants/syncNarrativeFolderIdentifier";
import { syncStatusOptions } from "../../constants/syncStatus";
import SyncTypeOptions from "../../constants/SyncTypeOptions";
import { appStateSelectors } from "../../modules/appState";
import propertySelectors from "../../modules/definition/properties/selectors";
import referenceSelectors from "../../modules/definition/references/selectors";
import typeSelectors from "../../modules/definition/types/selectors";
import itemSelectors from "../../modules/items/selectors";
import sortFunctions from "../../modules/items/sortFunctions";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import syncRepresentationOfNarrativeFromState from "../../sync/selectors/syncRepresentationOfNarrativeFromState";
import getSyncableRep from "../../sync/utils/getSyncableRep";
import removeMillisecondsFromDateString from "../../sync/utils/removeMillisecondsFromDateString";
import { syncStatusForAction } from "../../sync/utils/syncStatusForAction";
import { immutableRecords } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import treeUtils from "../../utils/tree";
import syncBinderSelectors from "../syncBinder/selectors";

const fieldLabelForId = (externalRep, syncStatus, fieldId, typesById, propertiesById, referencesById) => {
  switch (fieldId) {
    case "title":
      return "Title";
    case "synopsis":
      return "Synopsis";
    default: {
      // If Scrivener, a custom field should exist for that ID. Just grab its label.
      const field = externalRep.customFields.get(fieldId);
      if (field) {
        return field.label;
      }
      // For Ulysses, we just need to lookup the full name for the ID we use.
      if (syncLocalFieldNames[fieldId]) {
        return syncLocalFieldNames[fieldId];
      }
      // Fallback for brand new fields in Scrivener: they are not created yet, so we need to use the sync settings to work out which field they will be
      const statusField =
        syncStatus && syncStatus.updatedSettings
          ? syncStatus.updatedSettings.fields.find(f => f.customFieldId === fieldId)
          : null;
      if (statusField) {
        if (syncLocalFieldNames[statusField.id]) {
          return syncLocalFieldNames[statusField.id];
        }
        if (typesById.has(statusField.id)) {
          return typesById.get(statusField.id).label;
        }
        if (propertiesById.has(statusField.id)) {
          return propertiesById.get(statusField.id).label;
        }
        if (referencesById.has(statusField.id)) {
          return referencesById.get(statusField.id).label;
        }

        return statusField.id;
      }
      // If all else fails, show the field ID... but we hope we never get here!
      return fieldId;
    }
  }
};

const getOptionFromDocument = doc => {
  // TODO: Pass correct value for hasChildren
  const icon = getDefaultIconForSyncType(doc.type, false);

  return immutableRecords.DropdownOptionRecord({
    label: getLabelWithUntitled(doc.label),
    value: doc.id,
    color: "mono",
    icon
  });
};

const getOptionFromItem = (item, type) =>
  immutableRecords.DropdownOptionRecord({
    label: getLabelWithUntitled(item.label),
    value: item.id,
    color: type.color,
    icon: type.icon
  });

const createOption = immutableRecords.DropdownOptionRecord({
  label: "Create new",
  value: SyncTypeOptions.CREATE,
  color: "mono",
  icon: "statusAdded"
});

const ignoreOption = immutableRecords.DropdownOptionRecord({
  label: "Ignore",
  value: SyncTypeOptions.IGNORE,
  color: "mono",
  icon: "statusIgnore"
});

// /**
//  * Recently it filters out the narrative only type
//  * when the item doesn't have a narrative position
//  */
// const getAllowedTypeOptionsByItems = (state, itemIds) => {
//   const hasNoNarrativePositionItem =
//     itemIds.findIndex(id => narrativeSelectors.getNarrativePositionForItemId(state, id).size === 0) >= 0;
//   const types = hasNoNarrativePositionItem
//     ? typeSelectors.getOrderedNoNarrativeOnlyTypes(state)
//     : typeSelectors.getOrderedTypes(state);
//   return types.map(getTypeOptionFromType);
// };

const getUnlinkedDocumentOptionsForItem = createSelector(
  // Inputs
  syncSelectors.getExternalSyncRep,
  syncSelectors.getSyncedLocalToExternalIds,
  syncSelectors.getLocalToExternalIds,
  itemSelectors.getItemsById,
  typeSelectors.getTypesById,
  syncSelectors.getSyncSettings,
  appStateSelectors.getSelectedSyncItems,
  syncSelectors.getSyncStatus,
  // Output
  (
    externalRep,
    syncedLocalToExternalIds,
    localToExternalIds,
    itemsById,
    typesById,
    syncSettings,
    selectedItems,
    syncStatus
  ) => {
    const localId = selectedItems.first();
    const item = itemsById.get(localId);

    // If the item does not exist, or is already committed to an item link, then we don't
    // need to return any options
    if (
      !externalRep ||
      !item ||
      syncedLocalToExternalIds.has(localId) ||
      syncStatus.newPairs.find(p => p.localId === localId)
    ) {
      return null;
    }
    const type = typesById.get(item.type);
    let rootTree = null;
    // Find unlinked items inside narrative
    if (type.canIncludeInNarrative) {
      rootTree = treeUtils.getSubTreeWithId(externalRep.documentTree, SYNC_NARRATIVE_FOLDER_IDENTIFIER);
    } else {
      // TODO: This will only handle mainType right now.
      // Need to handle any type that could exist within the tree.
      const matchingFolder = syncSettings.entityFolders.find(f => f.mainType === type.id);
      if (matchingFolder && matchingFolder.folderId) {
        rootTree = treeUtils.getSubTreeWithId(externalRep.documentTree, matchingFolder.folderId);
      }
    }
    if (!rootTree) {
      return null;
    }
    const documentIds = List(treeUtils.getIdsInTree(rootTree));
    // We are looking for unlinked documents... we want to use localToExternalIds rather than syncedLocal...
    // because we don't want to present options the user has selected elsewhere either!
    return documentIds
      .filter(
        id => id !== rootTree.id && !localToExternalIds.find((externalId, lId) => externalId === id && lId !== localId)
      )
      .map(docId => {
        const doc = externalRep.documents.get(docId);
        return getOptionFromDocument(doc);
      })
      .insert(0, createOption);
  }
);

const getUnlinkedItemOptionsForDocument = createSelector(
  // Inputs
  itemSelectors.getItemsById,
  itemSelectors.getAllItemIdsAllowedInNarrative,
  syncSelectors.getSyncedLocalToExternalIds,
  syncSelectors.getLocalToExternalIds,
  typeSelectors.getTypesById,
  syncSelectors.getExternalSyncRep,
  syncSelectors.getSyncSettings,
  appStateSelectors.getSelectedSyncItems,
  syncSelectors.getSyncStatus,
  // Output
  (
    itemsById,
    itemIdsAllowedInNarrative,
    syncedLocalToExternalIds,
    localToExternalIds,
    typesById,
    externalRep,
    syncSettings,
    selectedItems,
    syncStatus
  ) => {
    const externalId = selectedItems.first();
    // If the Document does not exist, or is already committed to an item link, then we don't
    // need to return any options
    if (
      !externalRep ||
      syncedLocalToExternalIds.find(eId => eId === externalId) ||
      syncStatus.newPairs.find(p => p.externalId === externalId) ||
      !externalRep.documents.has(externalId)
    ) {
      return null;
    }

    const syncableExternalRep = getSyncableRep(externalRep, syncSettings, false);

    const rootFolderIds = syncSettingsSelectors.getSyncRootFolderIdsFromSettings(syncSettings);
    const rootFolder = treeUtils.getAncestorOfNodeFromIds(syncableExternalRep.documentTree, rootFolderIds, externalId);
    const rootFolderId = rootFolder ? rootFolder.id : null;
    const isNarrative = rootFolderId === SYNC_NARRATIVE_FOLDER_IDENTIFIER;

    if (isNarrative) {
      // We are looking for unlinked items allowed in narrative... we want to use localToExternalIds rather than syncedLocal...
      // because we don't want to present options the user has selected elsewhere either!
      const result = itemIdsAllowedInNarrative
        .map(id => itemsById.get(id))
        .filter(item => !localToExternalIds.has(item.id) || localToExternalIds.get(item.id) === externalId)
        .toList()
        .sort(sortFunctions.bySequence)
        .map(item => getOptionFromItem(item, typesById.get(item.type)));
      return result.insert(0, createOption).insert(1, ignoreOption);
    } else {
      // TODO: Before we can decide *what* to link with, we need to work out the type.
      // Which is now much more difficult than it used to be!

      // TODO: This needs to look at document type and depth to work out which options are legal
      const matchingFolder = syncSettings.entityFolders.get(rootFolderId);
      const type = matchingFolder ? typesById.get(matchingFolder.mainType) : null;
      if (type) {
        return itemsById
          .filter(item => !localToExternalIds.has(item.id) && item.type === type.id)
          .toList()
          .sort(sortFunctions.byLabel)
          .map(item => getOptionFromItem(item, type))
          .insert(0, createOption)
          .insert(1, ignoreOption);
      }
      return List([createOption, ignoreOption]);
    }
  }
);

const getSyncItemStatus = createSelector(
  // Inputs
  syncSelectors.getExternalSyncRep,
  syncRepresentationOfNarrativeFromState,
  syncSelectors.getSyncStatus,
  syncSelectors.getLocalToExternalIds,
  syncSelectors.getSyncIgnoreExternalIds,
  itemSelectors.getItemsById,
  typeSelectors.getTypesById,
  propertySelectors.getPropertiesById,
  referenceSelectors.getReferencesById,
  appStateSelectors.getSelectedSyncItems,
  syncSelectors.getSyncSettings,
  // Output
  (
    externalRep,
    localRep,
    syncStatus,
    localToExternalIds,
    ignoredExternalIds,
    itemsById,
    typesById,
    propertiesById,
    referencesById,
    selectedItems,
    syncSettings
  ) => {
    const id = selectedItems.first();
    if (!id || !externalRep) {
      return immutableRecords.InspectorSyncItemRecord({});
    }

    const isIgnored = ignoredExternalIds.has(id);

    const breadcrumbs = List(treeUtils.findBreadcrumbsOfNode(syncStatus.documentUpdates.visualMergeTree, id));

    const destinationPath = breadcrumbs.map(breadcrumbId =>
      externalRep.documents.has(breadcrumbId)
        ? externalRep.documents.get(breadcrumbId).label
        : localRep.documents.has(breadcrumbId)
          ? localRep.documents.get(breadcrumbId).label
          : "Untitled"
    );

    const documentUpdate = syncStatus.documentUpdates.updatesById.get(id);
    const status = documentUpdate ? documentUpdate.status : syncStatusOptions.DETECTED_NO_CHANGE;

    let localId = localToExternalIds.findKey(eId => eId === id);
    if (!localId) {
      const matchingPair = syncStatus.newPairs.find(p => p.externalId === id);
      localId = matchingPair ? matchingPair.localId : localId;
    }
    if (!localId && itemsById.has(id)) {
      localId = id;
    }

    const localItem = itemsById.get(localId);
    const localLabel = localItem
      ? localItem.label
      : status === syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL
        ? "Create new"
        : status === syncStatusOptions.DETECTED_IGNORE
          ? "Ignored Document"
          : "Deleted";
    const localType = localItem ? typesById.get(localItem.type) : null;
    const localTypeIcon = localType
      ? localType.icon
      : status === syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL
        ? "statusAdded"
        : "statusDeleted";
    const localTypeColor = localType
      ? localType.color
      : status === syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL
        ? "mono"
        : "iconFade";

    const externalDoc = externalRep.documents.get(id);
    const externalId = externalDoc ? id : null;
    const externalLabel = externalDoc
      ? externalDoc.label
      : status === syncStatusOptions.DETECTED_CREATE_IN_LOCAL
        ? "Create new"
        : "Deleted";
    const externalType = externalDoc ? externalDoc.type : null;
    // TODO: Pass correct value for hasChildren
    const externalTypeIcon = externalType
      ? getDefaultIconForSyncType(externalType, false)
      : status === syncStatusOptions.DETECTED_CREATE_IN_LOCAL
        ? "statusAdded"
        : "statusDeleted";
    const externalTypeColor = status !== syncStatusOptions.DETECTED_DELETE_IN_LOCAL ? "mono" : '"iconFade';
    let propertyChanges = [];
    let relationshipChanges = [];
    let tagChanges = [];

    const syncType = syncBinderSelectors.getSyncTypeForTreeItem(externalRep.documents, localRep.documents, id);
    // TODO: Pass correct value for hasChildren
    const iconForTreeItem = syncBinderSelectors.getIconForTreeItem(id, false, syncType, syncSettings, typesById);

    if (documentUpdate) {
      documentUpdate.propertyUpdates.forEach((propUpdate, key) => {
        const id = key;
        const label = fieldLabelForId(externalRep, syncStatus, key, typesById, propertiesById, referencesById);
        const action = propUpdate.action;
        const status = syncStatusForAction(action);
        const localValue = removeMillisecondsFromDateString(propUpdate.localValue);
        const externalValue = removeMillisecondsFromDateString(propUpdate.externalValue);
        const lastSyncValue = removeMillisecondsFromDateString(propUpdate.lastSyncValue);

        propertyChanges.push(
          immutableRecords.InspectorSyncItemPropertyChangeRecord({
            id,
            status,
            action,
            label,
            localValue,
            externalValue,
            lastSyncValue
          })
        );
      });

      documentUpdate.relationshipUpdates.forEach(relUpdate => {
        const itemId = relUpdate.id;
        const item = itemsById.get(itemId);
        const label = item ? item.label : relUpdate.label;
        const action = relUpdate.action;
        const status = syncStatusForAction(action);

        relationshipChanges.push(
          immutableRecords.InspectorSyncItemRelationshipChangeRecord({
            id,
            status,
            action,
            label
          })
        );
      });

      documentUpdate.tagUpdates.forEach(tagUpdate => {
        const label = tagUpdate.label;
        const action = tagUpdate.action;
        const status = syncStatusForAction(action);

        tagChanges.push(
          immutableRecords.InspectorSyncItemTagChangeRecord({
            status,
            action,
            label
          })
        );
      });
    }

    return immutableRecords.InspectorSyncItemRecord({
      id,
      isIgnored,
      status,
      destinationPath,
      externalId,
      externalLabel,
      externalType,
      externalTypeIcon,
      externalTypeColor,
      localId,
      localLabel,
      localTypeIcon,
      localTypeColor,
      propertyChanges: List(propertyChanges),
      relationshipChanges: List(relationshipChanges),
      tagChanges: List(tagChanges),
      iconForTreeItem
    });
  }
);

export default {
  getSyncItemStatus,
  getUnlinkedDocumentOptionsForItem,
  getUnlinkedItemOptionsForDocument
};
